import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Table, createCustomColumn, createDateColumn, createTextColumn, useTableState } from '@medprompt/shared';
import { MeasurementTypeEnum } from '../enums/measurementTypeEnum';
import { SimilarSearchEnum } from '../enums/similarSearchEnum';
import { GUIDANCE_CONTENT_TYPES } from './configConstants';
import {
  asDisabledFilterSet,
  renderOptionalCell,
  renderSnomedCodeCell,
} from './configTableCells';
import ConfiguredCodeSection from './ConfiguredCodeSection';
import EditMeasurementCodeModal from './EditMeasurementCodeModal';

const GeneratorConfigSection = ({
  config,
  isEditingConfig,
  saving,
  selectedTypes,
  toggleSearchType,
  clinicalDepartments,
  letterTypes,
  measurements,
  handleCodeSearch,
  documentContentTypeToGuidance,
  onGuidanceChange,
  onGuidanceClear,
  shouldExtractExtendedMeasurements,
  setShouldExtractExtendedMeasurements,
  shouldBundleBloodPressureReadings,
  setShouldBundleBloodPressureReadings,
}) => {
  const {
    sortState: frequentCodesSortState,
    setSortState: setFrequentCodesSortState,
    filterValues: frequentCodesFilterValues,
    setFilterValues: setFrequentCodesFilterValues,
    disabledFilters: frequentCodesDisabledFilters,
    setDisabledFilters: setFrequentCodesDisabledFilters,
    paginationState: frequentCodesPaginationState,
    setPaginationState: setFrequentCodesPaginationState,
    highlightedRows: frequentCodesHighlightedRows,
    setHighlightedRows: setFrequentCodesHighlightedRows
  } = useTableState('FrequentCodesConfigTable', {
    defaultSortState: { columnKey: null, direction: null }
  });

  const frequentCodesTableRows = useMemo(() => {
    const list = config?.generatorConfig?.frequentCodes;
    if (!Array.isArray(list)) return [];

    return list.map((frequentCode, index) => {
      const codeRecord = frequentCode?.code;
      const codeId = codeRecord?.code != null ? String(codeRecord.code) : '';
      const preferredTerm =
        Array.isArray(codeRecord?.terms) && codeRecord.terms.length > 0
          ? codeRecord.terms[0]
          : '';
      return {
        id: codeId || `frequent-code-${index}`,
        code: codeId,
        preferredTerm,
        count: frequentCode?.count ?? 0,
        lastAddedAt: frequentCode?.lastAddedAt ?? null,
      };
    });
  }, [config?.generatorConfig?.frequentCodes]);

  const frequentCodesColumns = useMemo(
    () => [
      createCustomColumn(
        'code',
        'SNOMED CT code',
        (row) => row.code ?? '',
        renderSnomedCodeCell,
        { sortable: true, filterable: true }
      ),
      createCustomColumn(
        'preferredTerm',
        'Term',
        (row) => row.preferredTerm ?? '',
        renderOptionalCell,
        { sortable: true, filterable: true }
      ),
      createTextColumn(
        'count',
        'Count',
        (row) => row.count,
        {
          sortable: true,
          filterable: true,
          compareFn: (a, b) => (a.count ?? 0) - (b.count ?? 0),
          renderer: (value) => String(value ?? 0),
        }
      ),
      createDateColumn(
        'lastAddedAt',
        'Last used',
        (row) => row.lastAddedAt,
        { sortable: true, filterable: true }
      ),
    ],
    []
  );

  return (
    <div className="config-section">
      <div className="section-header">
        <h2>Document Generator Configuration</h2>
      </div>

      <div className="section-content">
        <div className="config-subsection">
          <h3>Included Code Types</h3>
          <div className="toggle-buttons">
            {Object.keys(SimilarSearchEnum).map(type => (
              <button
                key={type}
                className={`toggle-button ${selectedTypes.includes(type) ? 'active' : ''} ${!isEditingConfig ? 'disabled' : ''}`}
                onClick={() => isEditingConfig && toggleSearchType(type)}
                style={{
                  backgroundColor: selectedTypes.includes(type) ? SimilarSearchEnum[type].color : 'transparent',
                  cursor: isEditingConfig ? 'pointer' : 'default'
                }}
                disabled={saving}
              >
                {selectedTypes.includes(type) && (
                  <FontAwesomeIcon icon={faCheck} className="check-icon" />
                )}
                {SimilarSearchEnum[type].description}
              </button>
            ))}
          </div>
        </div>

        <ConfiguredCodeSection
          list={clinicalDepartments}
          isEditingConfig={isEditingConfig}
          saving={saving}
          handleCodeSearch={handleCodeSearch}
          defaultHeading="Default clinical department"
          defaultDescription="Default SNOMED code when assigning a clinical department to a document."
          defaultName="Default clinical department"
          defaultSearchPlaceholder="Search for default clinical department code…"
          listHeading="Clinical department codes"
          listDescription="The list of SNOMED codes MedPrompt will choose from when processing a document. Click a row while editing to edit its term."
          addButtonTitle="Add a clinical department code"
          emptyMessage="No clinical department codes to display."
          addModalTitle="Add clinical department code"
          freeTextPlaceholder="e.g. Cardiothoracic surgery"
          removeModalTitle="Remove clinical department codes"
          editModalTitle="Clinical department code"
          editInputId="clinical-dept-configured-code-term-input"
        />

        <ConfiguredCodeSection
          list={letterTypes}
          isEditingConfig={isEditingConfig}
          saving={saving}
          handleCodeSearch={handleCodeSearch}
          defaultHeading="Default letter type"
          defaultDescription="Default SNOMED code when assigning a letter type to a document."
          defaultName="Default letter type"
          defaultSearchPlaceholder="Search for default letter type code…"
          listHeading="Letter type codes"
          listDescription="The list of SNOMED codes MedPrompt will choose from when assigning a letter type. Click a row while editing to edit its term."
          addButtonTitle="Add a letter type code"
          emptyMessage="No letter type codes to display."
          addModalTitle="Add letter type code"
          freeTextPlaceholder="e.g. Discharge summary"
          removeModalTitle="Remove letter type codes"
          editModalTitle="Letter type code"
          editInputId="letter-type-configured-code-term-input"
        />

        <div className="config-subsection">
          <h3>Custom AI guidance</h3>
          <div className="retention-description" style={{ marginBottom: '12px' }}>
            Practice-specific instructions appended when classifying letter type, clinical department, and urgency. Leave blank to use MedPrompt defaults.
          </div>
          <div className="guidance-cards">
            {GUIDANCE_CONTENT_TYPES.map(({ key, label, description }) => {
              const value = documentContentTypeToGuidance[key] ?? '';
              const hasValue = value.length > 0;
              return (
                <div key={key} className="measurement-code-item guidance-card">
                  <div className="measurement-info">
                    <div className="measurement-header">
                      <div className="measurement-details">
                        <div className="measurement-name">{label}</div>
                        <div className="measurement-description">{description}</div>
                      </div>
                      {isEditingConfig && hasValue && (
                        <div className="measurement-controls">
                          <button
                            type="button"
                            className="other-button cancel-edit-button"
                            onClick={() => onGuidanceClear(key)}
                            disabled={saving}
                            title="Clear guidance"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      )}
                    </div>
                    {isEditingConfig ? (
                      <textarea
                        className="config-guidance-textarea"
                        value={value}
                        onChange={(e) => onGuidanceChange(key, e.target.value)}
                        disabled={saving}
                        rows={4}
                        placeholder="Optional practice-specific instructions…"
                      />
                    ) : hasValue ? (
                      <div className="config-guidance-readonly">{value}</div>
                    ) : (
                      <div className="config-guidance-empty">No custom guidance</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="section-content">
        <div className="config-subsection">
          <h3>Measurement extraction</h3>
          <label
            className={`config-checkbox-row ${!isEditingConfig || saving ? 'is-disabled' : ''}`}
          >
            <input
              type="checkbox"
              checked={shouldExtractExtendedMeasurements}
              onChange={(e) =>
                isEditingConfig &&
                !saving &&
                setShouldExtractExtendedMeasurements(e.target.checked)
              }
              disabled={!isEditingConfig || saving}
              className="config-setting-checkbox"
            />
            <span>Extract extended measurements from documents</span>
          </label>
          <div className="retention-description">
            When enabled, additional laboratory and clinical measurements beyond the core set are extracted during document processing.
          </div>
          <label
            className={`config-checkbox-row ${!isEditingConfig || saving ? 'is-disabled' : ''}`}
          >
            <input
              type="checkbox"
              checked={shouldBundleBloodPressureReadings}
              onChange={(e) =>
                isEditingConfig &&
                !saving &&
                setShouldBundleBloodPressureReadings(e.target.checked)
              }
              disabled={!isEditingConfig || saving}
              className="config-setting-checkbox"
            />
            <span>Bundle blood pressure readings</span>
          </label>
          <div className="retention-description">
            When enabled, systolic and diastolic values are bundled together into a single reading during measurement processing.
          </div>
        </div>

        <div className="config-subsection clinical-dept-codes-subsection">
          <div className="clinical-dept-subsection-head">
            <div>
              <h3>Codes for measurements</h3>
              <div className="retention-description">
                SNOMED codes used when filing extracted measurements. Click a row while editing to change its code or edit its term.
              </div>
            </div>
          </div>
          <div className="clinical-dept-table-wrap config-table-container">
            <Table
              id="MeasurementCodesConfigTable"
              data={measurements.tableRows}
              columns={measurements.columns}
              getRowKey={(row) => row.measurementType}
              sortState={measurements.sortState}
              onSortChange={measurements.setSortState}
              filterValues={measurements.filterValues}
              onFilterChange={measurements.setFilterValues}
              disabledFilters={asDisabledFilterSet(measurements.disabledFilters)}
              onDisabledFiltersChange={measurements.setDisabledFilters}
              paginationState={measurements.paginationState}
              onPaginationChange={measurements.setPaginationState}
              highlightedRows={measurements.highlightedRows}
              onHighlightChange={measurements.setHighlightedRows}
              onRowClick={
                isEditingConfig && !saving
                  ? measurements.handleMeasurementRowClick
                  : undefined
              }
              emptyMessage="No measurements to display."
            />
          </div>
        </div>

        <div className="config-subsection clinical-dept-codes-subsection">
          <div className="clinical-dept-subsection-head">
            <div>
              <h3>Frequently used codes</h3>
              <div className="retention-description">
                Codes that users at this practice commonly search for and add to documents. This list is learned over time.
              </div>
            </div>
          </div>
          <div className="clinical-dept-table-wrap config-table-container">
            <Table
              id="FrequentCodesConfigTable"
              data={frequentCodesTableRows}
              columns={frequentCodesColumns}
              getRowKey={(row) => row.id}
              sortState={frequentCodesSortState}
              onSortChange={setFrequentCodesSortState}
              filterValues={frequentCodesFilterValues}
              onFilterChange={setFrequentCodesFilterValues}
              disabledFilters={asDisabledFilterSet(frequentCodesDisabledFilters)}
              onDisabledFiltersChange={setFrequentCodesDisabledFilters}
              paginationState={frequentCodesPaginationState}
              onPaginationChange={setFrequentCodesPaginationState}
              highlightedRows={frequentCodesHighlightedRows}
              onHighlightChange={setFrequentCodesHighlightedRows}
              emptyMessage="No frequently used codes yet."
            />
          </div>
        </div>
      </div>

      <EditMeasurementCodeModal
        isOpen={!!measurements.editingMeasurement}
        onClose={() => measurements.setEditingMeasurement(null)}
        measurementInfo={
          measurements.editingMeasurement ? MeasurementTypeEnum[measurements.editingMeasurement] : null
        }
        currentCode={
          measurements.editingMeasurement
            ? measurements.measurementCodes[measurements.editingMeasurement]
            : null
        }
        onPick={(result) => measurements.handleMeasurementCodeSelect(measurements.editingMeasurement, result)}
        onSaveTerm={measurements.handleMeasurementCodeSave}
        handleCodeSearch={handleCodeSearch}
      />
    </div>
  );
};

export default GeneratorConfigSection;
